/* eslint-disable @typescript-eslint/no-explicit-any */
import { Parser, parseAsArrayOf, parseAsInteger, parseAsJson, parseAsString } from 'nuqs';
import { z } from 'zod';
import {
  BackendKey,
  Connector,
  FilterDef,
  PageFilterDef,
  PaginationFilterDef,
  PaginationSchema,
  RegularFilterDef,
  SortFilterDef,
} from './types';

/**
 * Creates a filter definition based on the provided key, lookup, parser, and connector.
 *
 * @param key The key for the filter
 * @param def The filter definition object containing lookup, parser, and connector
 * @returns The filter definition
 */
export function filter<K extends string, L extends BackendKey<K>, T extends Parser<any>>(
  key: K,
  def: {
    lookup: L;
    parser: T;
    connector: Exclude<Connector, 'sort' | 'pagination'>;
  },
): RegularFilterDef<K, T> {
  return { key, ...def };
}

export const TagsFilterSchema = z
  .array(z.object({ tagType: z.string(), tags: z.array(z.string()) }))
  .optional();
export type TagsFilter = z.infer<typeof TagsFilterSchema>;

/**
 * Reusable filter for tags (OR within TagTypes, AND across TagTypes)
 */
export function tagsFilter() {
  return filter('tags', {
    lookup: 'tags',
    parser: parseAsJson(TagsFilterSchema.parse).withDefault([]),
    connector: 'custom',
  });
}

/**
 * Creates a filter definition for sorting based on the provided options.
 *
 * @param options The options for sorting, including a default array of strings
 */
export function sortFilter(options?: { default?: string[] }): SortFilterDef {
  const defaultValue = options?.default || [];
  return {
    key: 'sort',
    parser: parseAsArrayOf(parseAsString).withDefault(defaultValue),
    connector: 'sort',
  };
}

/**
 * Creates a filter definition for page pagination (offset)
 *
 * @param options The options for pagination, including a default number
 */
export function pageFilter(options?: { default?: number }): PageFilterDef {
  const defaultValue = options?.default || 1;
  return {
    key: 'page',
    parser: parseAsInteger.withDefault(defaultValue),
    connector: 'page',
  };
}

/**
 * Creates a filter definition for pagination (limit, offset)
 *
 * @param options The options for pagination, including a default limit and offset
 */
export function limitOffsetFilter(options?: {
  default?: {
    limit?: number;
    offset?: number;
  };
}): PaginationFilterDef {
  const defaultLimit = options?.default?.limit ?? 10;
  const defaultOffset = options?.default?.offset ?? 1;

  const parser = parseAsJson(PaginationSchema.parse).withDefault({
    limit: defaultLimit,
    offset: defaultOffset,
  });

  return {
    key: 'pagination',
    parser,
    connector: 'pagination',
  };
}

/**
 * Creates filters based on the provided definitions
 *
 * @template T The type of filter definitions array
 * @param defs The array of filter definitions
 */
export function createFilters<T extends readonly FilterDef<string, Parser<any>>[]>(...defs: T) {
  type Def = T[number];
  type Key = Def['key'];
  type Result = {
    [K in Key]: Extract<Def, { key: K }>['parser'];
  };

  const parsers = Object.fromEntries(defs.map((d) => [d.key, d.parser])) as Result;

  return {
    defs,
    parsers,
  };
}

export interface FiltersByConnector extends Record<Connector, Record<string, any>> {
  withMerge(additionalFilters: Partial<Record<Connector, Record<string, any>>>): FiltersByConnector;
}

/**
 * This function deep merges the target object with the source object, preserving nested structures and arrays.
 *
 * @template T The type of the target object
 * @param target The target object to merge into
 * @param source The source object to merge from
 * @returns The merged object
 */
function deepMerge<T extends Record<string, any>>(target: T, source: Partial<T>): T {
  const result = { ...target };

  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      if (source[key] && typeof source[key] === 'object') {
        if (Array.isArray(source[key])) {
          if (Array.isArray(result[key])) {
            const sourceArray = source[key] as unknown as any[];
            const resultArray = result[key] as unknown as any[];

            const isPrimitive: (val: any) => boolean = (val: any) =>
              typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean';

            const isArrayOfArrays =
              sourceArray.length > 0 &&
              Array.isArray(sourceArray[0]) &&
              resultArray.length > 0 &&
              Array.isArray(resultArray[0]);

            if (isArrayOfArrays) {
              const uniqueArrays = [...resultArray];

              const stringifyArray = (arr: any[]) => JSON.stringify(arr.sort());

              const existingArraysSet = new Set(uniqueArrays.map((arr) => stringifyArray(arr)));

              sourceArray.forEach((arr) => {
                const stringified = stringifyArray(arr);
                if (!existingArraysSet.has(stringified)) {
                  uniqueArrays.push(arr);
                  existingArraysSet.add(stringified);
                }
              });

              result[key] = uniqueArrays as any;
            } else if (sourceArray.length > 0 && isPrimitive(sourceArray[0])) {
              result[key] = [...new Set([...resultArray, ...sourceArray])] as any;
            } else {
              result[key] = resultArray.concat(sourceArray) as any;
            }
          } else {
            result[key] = source[key] as any;
          }
        } else {
          result[key] = deepMerge(result[key] || ({} as any), source[key] as any) as any;
        }
      } else {
        result[key] = source[key] as any;
      }
    }
  }

  return result;
}

export interface BackendFiltersOptions {
  mergeWith?: Partial<Record<Connector, Record<string, any>>> & {
    filter_and_or?: Record<string, any>;
  };
  omit?: Partial<Record<Connector, boolean>> & {
    filter_and_or?: boolean;
  }; // TODO: Improve omit to accept properties inside Connector
  override?: Partial<Record<Connector, Record<string, any>>> & {
    filter_and_or?: Record<string, any>;
  };
}

/**
 * Transforms a set of frontend filters into a structure that can be directly consumed by the backend API.
 *
 * This function takes filter definitions and their parsed values, and organizes them into the appropriate
 * connector categories (filter, filter_or, filter_and, etc.) as expected by the backend. It also handles
 * special cases like sorting, pagination, and page parameters.
 *
 * The function supports:
 * - Merging with existing filter values via the `mergeWith` option
 * - Overriding specific filters via the `override` option
 * - Omitting specific connector types via the `omit` option
 *
 * @param defs - Array of filter definitions that specify how each filter should be processed
 * @param filters - Object containing the parsed filter values, keyed by the filter definition keys
 * @param options - Optional configuration for handling the filters (merging, overriding, omitting)
 * @returns A structured object ready to be sent directly to the backend API, with filters organized by connector type
 */
export function createBackendFilters<
  Defs extends readonly FilterDef<string, Parser<any>>[],
  Parsed extends { [K in Defs[number] as K['key']]: ReturnType<K['parser']['parse']> },
>(
  defs: Defs,
  filters: Parsed,
  options?: BackendFiltersOptions,
): Record<Connector, Record<string, any>> {
  const filtersByConnector: Record<Connector, Record<string, any>> & {
    filter_and_or: Record<string, any>;
  } = {
    filter: {},
    filter_or: {},
    filter_and: {},
    filter_and_or: {},
    exclude: {},
    exclude_or: {},
    custom: {},
    sort: {},
    pagination: {},
    page: {},
  };

  const topLevelParams: { order?: string; limit?: number; offset?: number } = {};
  let sortValues: string[] = [];

  for (const def of defs) {
    const value = filters[def.key as Defs[number]['key']];
    if (value === undefined || value === null || (Array.isArray(value) && value.length === 0)) {
      continue;
    }

    const connector = def.connector;

    if (options?.omit?.[connector]) {
      continue;
    }

    if (connector === 'sort') {
      if (Array.isArray(value)) {
        sortValues = [...sortValues, ...value];
      } else if (typeof value === 'string') {
        sortValues.push(value);
      }
    } else if (connector === 'pagination') {
      if (def.key === 'pagination' && typeof value === 'object') {
        if ('limit' in value && typeof value.limit === 'number') {
          topLevelParams.limit = value.limit;
        }
        if ('offset' in value && typeof value.offset === 'number') {
          topLevelParams.offset = value.offset;
        }
      }
    } else if (connector === 'page') {
      if (def.key === 'page' && typeof value === 'number') {
        const offset = value - 1 < 0 ? 0 : value - 1;
        topLevelParams.offset = offset;
      }
    } else {
      const regularDef = def as RegularFilterDef<string, Parser<any>>;
      filtersByConnector[connector][regularDef.lookup] = value;
    }
  }

  if (options?.mergeWith) {
    for (const connector in options.mergeWith) {
      if (options?.omit?.[connector as Connector]) {
        continue;
      }

      const mergeWithConnector = options.mergeWith[connector as Connector];
      if (!mergeWithConnector) {
        continue;
      }

      if (connector === 'sort') {
        if (Array.isArray(mergeWithConnector)) {
          sortValues = [...mergeWithConnector, ...sortValues];
        } else if (typeof mergeWithConnector === 'string') {
          sortValues.unshift(mergeWithConnector);
        }
        continue;
      }

      if (Object.prototype.hasOwnProperty.call(filtersByConnector, connector)) {
        const typedConnector = connector as Connector;

        const cleanedMergeWith: Record<string, unknown> = {};
        for (const key in mergeWithConnector) {
          if (mergeWithConnector[key] !== undefined) {
            cleanedMergeWith[key] = mergeWithConnector[key];
          }
        }

        if (Object.keys(cleanedMergeWith).length > 0) {
          filtersByConnector[typedConnector] = deepMerge(
            filtersByConnector[typedConnector],
            cleanedMergeWith,
          );
        }
      }
    }
  }

  const result: Record<string, any> = {};

  for (const connector in filtersByConnector) {
    if (!options?.omit?.[connector as Connector]) {
      if (Object.keys(filtersByConnector[connector as Connector]).length > 0) {
        result[connector] = filtersByConnector[connector as Connector];
      }
    }
  }

  if (options?.override) {
    for (const connector in options.override) {
      if (options?.omit?.[connector as Connector]) {
        continue;
      }

      const overrideConnector = options.override[connector as Connector];
      if (!overrideConnector) {
        continue;
      }

      if (connector === 'pagination') {
        if ('limit' in overrideConnector && overrideConnector.limit !== undefined) {
          topLevelParams.limit = overrideConnector.limit;
        }
        if ('offset' in overrideConnector && overrideConnector.offset !== undefined) {
          topLevelParams.offset = overrideConnector.offset;
        }
      } else if (connector === 'sort') {
        if (Array.isArray(overrideConnector)) {
          sortValues = [...overrideConnector];
        } else if (typeof overrideConnector === 'string') {
          sortValues = [overrideConnector];
        }
      } else if (Object.prototype.hasOwnProperty.call(filtersByConnector, connector)) {
        const typedConnector = connector as Connector;
        filtersByConnector[typedConnector] = { ...overrideConnector };
      }
    }
  }

  if (!options?.omit?.['sort'] && sortValues.length > 0) {
    result.order = sortValues.join(',');
  }

  if (!options?.omit?.['pagination']) {
    if (topLevelParams.limit !== undefined) {
      result.limit = topLevelParams.limit;
    }
    if (topLevelParams.offset !== undefined) {
      result.offset = topLevelParams.offset;
    }
  }

  if (!options?.omit?.['page']) {
    if (topLevelParams.offset !== undefined) {
      result.offset = topLevelParams.offset;
    }
  }

  return result;
}
